import type { Prisma } from '@prisma/client';
import { getDb } from './db.js';

export const DOC_TYPES = {
  quotation: { prefix: 'QUO' },
  proforma: { prefix: 'PRO' },
  invoice: { prefix: 'INV' },
  receipt: { prefix: 'RCT' },
  creditnote: { prefix: 'CN' },
  purchaseorder: { prefix: 'PO' },
  bill: { prefix: 'BILL' },
  payout: { prefix: 'PAY' },
  expense: { prefix: 'EXP' },
  journal: { prefix: 'JV' },
  transfer: { prefix: 'TRF' },
} as const;

export type DocType = keyof typeof DOC_TYPES;

const ensured = new Set<string>();
/** Test helper: forget which counter rows are known to exist. */
export const resetSequenceCache = () => ensured.clear();

/**
 * Gapless, concurrency-safe document number, e.g. INV-2026-0001.
 * MUST be called inside the same transaction that saves the document: the counter row is locked
 * with SELECT ... FOR UPDATE, so concurrent callers queue up and a rolled-back transaction gives
 * the number back (no gaps).
 */
export async function nextDocumentNumber(
  tx: Prisma.TransactionClient,
  companyId: number,
  docType: DocType,
  date: Date = new Date(),
): Promise<string> {
  const year = date.getUTCFullYear();
  const defaults = DOC_TYPES[docType];

  // The counter row is created on a separate autocommit connection BEFORE it is locked (creating it
  // inside the transaction lets concurrent first-use callers deadlock on InnoDB gap locks). It is done
  // once per key: repeating it inside a transaction that already holds the row lock would make that
  // other connection wait on its own caller.
  const key = `${companyId}:${docType}:${year}`;
  const ensure = () =>
    getDb().$executeRaw`INSERT IGNORE INTO document_sequences
      (companyId, docType, year, prefix, padding, lastNumber)
      VALUES (${companyId}, ${docType}, ${year}, ${defaults.prefix}, 4, 0)`;
  const select = () =>
    tx.$queryRaw<
      { lastNumber: number; prefix: string; padding: number }[]
    >`SELECT lastNumber, prefix, padding FROM document_sequences
      WHERE companyId = ${companyId} AND docType = ${docType} AND year = ${year} FOR UPDATE`;

  if (!ensured.has(key)) {
    await ensure();
    ensured.add(key);
  }
  let rows = await select();
  if (rows.length === 0) {
    // Only after the table was emptied behind our back (tests): create it again and retry.
    await ensure();
    rows = await select();
  }
  const row = rows[0];
  if (!row) throw new Error('Document sequence row missing');

  const next = row.lastNumber + 1;
  // Raw UPDATE on purpose: the row was created on another connection, and Prisma's read-back after
  // update() would use this transaction's older snapshot and wrongly report "record not found".
  await tx.$executeRaw`UPDATE document_sequences SET lastNumber = ${next}
    WHERE companyId = ${companyId} AND docType = ${docType} AND year = ${year}`;
  return `${row.prefix}-${year}-${String(next).padStart(row.padding, '0')}`;
}
